import { useCallback, useEffect, useRef } from 'react';
import { useExternalTokenAuth, useGetOrganization } from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { useAgentSessionStore } from '@spendbrain/common/stores';
import { jwtDecode, JwtPayload } from 'jwt-decode';

const ONE_SECOND_MS = 1000;
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
/** Refresh this far before JWT `exp` so the iframe cookie is renewed in time. */
const REFRESH_BEFORE_EXPIRY_MS = 10 * ONE_MINUTE_MS;
const MIN_REFRESH_DELAY_MS = ONE_SECOND_MS;
/** Used when the minted token has no `exp` claim (or is not a JWT). */
const FALLBACK_REFRESH_INTERVAL_MS = 45 * ONE_MINUTE_MS;
/** Retry minting after a failed refresh without tearing down the current session. */
const REFRESH_RETRY_DELAY_MS = 30 * ONE_SECOND_MS;
const AGENT_TOKEN_AUTH_RETRY_COUNT = 2;

interface MintScope {
  orgId: string;
  aiPort: string | null;
}

const getTokenExpiryMs = (token: string): number | null => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);

    return exp ? exp * ONE_SECOND_MS : null;
  } catch {
    return null;
  }
};

const getMsUntilRefresh = (
  token: string,
  mintedAtMs: number | null
): number => {
  const expiresAtMs = getTokenExpiryMs(token);
  if (expiresAtMs === null) {
    if (mintedAtMs === null) return FALLBACK_REFRESH_INTERVAL_MS;

    return Math.max(
      mintedAtMs + FALLBACK_REFRESH_INTERVAL_MS - Date.now(),
      MIN_REFRESH_DELAY_MS
    );
  }

  return Math.max(
    expiresAtMs - Date.now() - REFRESH_BEFORE_EXPIRY_MS,
    MIN_REFRESH_DELAY_MS
  );
};

const shouldRefreshNow = (
  token: string,
  mintedAtMs: number | null
): boolean => {
  const expiresAtMs = getTokenExpiryMs(token);
  if (expiresAtMs === null) {
    if (mintedAtMs === null) return false;

    return Date.now() - mintedAtMs >= FALLBACK_REFRESH_INTERVAL_MS;
  }

  return expiresAtMs - Date.now() - REFRESH_BEFORE_EXPIRY_MS < 0;
};

export const useExternalToken = () => {
  const organizationId = useUserSetupStore(state => state.organization.id);
  const { data: organizationData } = useGetOrganization(organizationId);
  const agentToken = useAgentSessionStore(state => state.agentToken);
  const refreshNonce = useAgentSessionStore(state => state.refreshNonce);
  const setAgentToken = useAgentSessionStore(state => state.setAgentToken);
  const setStatus = useAgentSessionStore(state => state.setStatus);
  const mintScopeRef = useRef<MintScope | null>(null);
  const mintedAtMsRef = useRef<number | null>(null);
  const mintInFlightRef = useRef<Promise<boolean> | null>(null);
  const mintInFlightOrgIdRef = useRef<string | null>(null);

  const { mutateAsync: fetchAgentToken } = useExternalTokenAuth(
    organizationData?.aiPort,
    {
      retry: AGENT_TOKEN_AUTH_RETRY_COUNT,
    }
  );

  const mintToken = useCallback(async (): Promise<boolean> => {
    if (!organizationId) return false;

    // Coalesce concurrent mints for the same org (timer + visibility).
    // A different org must start a new request so a switch is not stuck
    // behind a stale in-flight mint that will be ignored on success.
    if (
      mintInFlightRef.current &&
      mintInFlightOrgIdRef.current === organizationId
    ) {
      return mintInFlightRef.current;
    }

    const requestedOrgId = organizationId;
    mintInFlightOrgIdRef.current = requestedOrgId;

    const mintPromise = (async (): Promise<boolean> => {
      try {
        if (!useAgentSessionStore.getState().agentToken) {
          setStatus('loading');
        }

        const data = await fetchAgentToken({ orgId: requestedOrgId });

        if (useUserSetupStore.getState().organization.id !== requestedOrgId) {
          return false;
        }

        const token = data?.access_token;
        if (token && typeof token === 'string') {
          mintedAtMsRef.current = Date.now();
          setAgentToken(token);
          setStatus('ready');

          return true;
        }

        if (!useAgentSessionStore.getState().agentToken) {
          setStatus('error');
        }

        return false;
      } catch {
        if (
          useUserSetupStore.getState().organization.id === requestedOrgId &&
          !useAgentSessionStore.getState().agentToken
        ) {
          setStatus('error');
        }

        return false;
      } finally {
        if (mintInFlightOrgIdRef.current === requestedOrgId) {
          mintInFlightRef.current = null;
          mintInFlightOrgIdRef.current = null;
        }
      }
    })();

    mintInFlightRef.current = mintPromise;

    return mintPromise;
  }, [organizationId, fetchAgentToken, setAgentToken, setStatus]);

  useEffect(() => {
    if (!organizationId || !organizationData?.aiEnabled) return;

    const aiPort = organizationData.aiPort ?? null;
    const previousScope = mintScopeRef.current;
    const orgChanged = previousScope?.orgId !== organizationId;
    const scopeChanged = orgChanged || previousScope?.aiPort !== aiPort;

    if (!scopeChanged) return;

    if (orgChanged) {
      mintedAtMsRef.current = null;
      setAgentToken(null);
    }

    let cancelled = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const mintForScope = () => {
      mintToken().then(success => {
        if (cancelled) return;

        if (!success) {
          retryTimeoutId = setTimeout(mintForScope, REFRESH_RETRY_DELAY_MS);

          return;
        }

        if (useUserSetupStore.getState().organization.id !== organizationId) {
          return;
        }

        mintScopeRef.current = { orgId: organizationId, aiPort };
      });
    };

    mintForScope();

    return () => {
      cancelled = true;
      if (retryTimeoutId !== undefined) clearTimeout(retryTimeoutId);
    };
  }, [
    organizationId,
    organizationData?.aiEnabled,
    organizationData?.aiPort,
    mintToken,
    setAgentToken,
  ]);

  useEffect(() => {
    if (!agentToken || !organizationId || !organizationData?.aiEnabled) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    // Successful mint updates `agentToken` / `refreshNonce` and re-runs this effect.
    const scheduleRefresh = (delay: number) => {
      timeoutId = setTimeout(() => {
        mintToken().then(success => {
          if (!cancelled && !success) scheduleRefresh(REFRESH_RETRY_DELAY_MS);
        });
      }, delay);
    };

    scheduleRefresh(getMsUntilRefresh(agentToken, mintedAtMsRef.current));

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    refreshNonce,
    agentToken,
    organizationId,
    organizationData?.aiEnabled,
    mintToken,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState !== 'visible' ||
        !organizationId ||
        !organizationData?.aiEnabled ||
        !agentToken
      ) {
        return;
      }

      if (shouldRefreshNow(agentToken, mintedAtMsRef.current)) mintToken();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [agentToken, organizationId, organizationData?.aiEnabled, mintToken]);

  return {
    agentToken,
    isAiEnabled: organizationData?.aiEnabled ?? false,
    organizationData,
  };
};
